import React from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  RefreshCcw,
  X,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui';
import { formatDistance } from '../../utils/dateHelpers';
import type { OsrmPreview } from '../../hooks/useOsrmCorrection';

/**
 * Floating card shown once an OSRM path has been computed for a segment
 * (docs/location-tracking/osrm-optimization.md §"Typical Workflow" steps
 * 2–4). The reviewer sees the corrected distance and can:
 *
 *   - Apply / Reject the correction (POST /insert-osrm-path).
 *   - Sync the day's summary after applying (POST /sync-osrm-data).
 *     Sync is only offered when the reviewer applied the path — a reject
 *     doesn't change the modified distance, so no re-sum is needed.
 *
 * The card sits over the map (top-right), non-modal, so the reviewer can
 * still pan / zoom the map to compare the dashed OSRM overlay against
 * the raw path.
 */

interface OsrmPreviewPanelProps {
  preview: OsrmPreview;
  /** Meters — raw distance for the same segment; shown alongside the
   *  OSRM number so the reviewer can see the delta. */
  rawDistanceMeters: number;
  /** `true` after the reviewer clicks Apply so we can pivot the CTA to
   *  "Sync summary" instead of showing Apply again. */
  busy: 'match' | 'apply' | 'reject' | 'sync' | null;
  error: { message: string; status?: number; code?: string } | null;
  /** `true` when today's / a future date is selected — the sync
   *  endpoint rejects those (only past dates can be synced). */
  syncDisabledReason?: string | null;
  onApply: () => void;
  onReject: () => void;
  onSync: () => void;
  onDismiss: () => void;
}

const formatMeters = (m: number): string => {
  // Reuse the same formatter the tracking module uses everywhere so the
  // units read the same on the map, in the panel, and in reports.
  return formatDistance(m / 1000);
};

export const OsrmPreviewPanel: React.FC<OsrmPreviewPanelProps> = ({
  preview,
  rawDistanceMeters,
  busy,
  error,
  syncDisabledReason,
  onApply,
  onReject,
  onSync,
  onDismiss,
}) => {
  const applied = preview.decision === 'apply';
  const rejected = preview.decision === 'reject';
  const delta = preview.distanceMeters - rawDistanceMeters;
  const deltaPrefix = delta >= 0 ? '+' : '−';

  return (
    <div
      className="absolute top-4 right-4 z-[1000] w-80 rounded-lg border border-rgf-gray-200 bg-white shadow-lg"
      role="dialog"
      aria-label="Path correction preview"
    >
      <div className="flex items-start justify-between px-4 py-3 border-b border-rgf-gray-100">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-rgf-gray-500">
            Path Correction
          </p>
          <p className="text-sm font-semibold text-rgf-gray-900">
            Segment #{preview.segmentId}
          </p>
          {preview.extended && (
            <p className="text-[11px] text-rgf-gray-500 mt-0.5">
              Extended to next segment
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="p-1 -m-1 rounded text-rgf-gray-400 hover:text-rgf-gray-700 hover:bg-rgf-gray-100"
          aria-label="Dismiss preview"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="px-4 py-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-rgf-gray-500">Raw distance</span>
          <span className="text-sm font-medium text-rgf-gray-800">
            {formatMeters(rawDistanceMeters)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-rgf-gray-500">Estimated distance</span>
          <span className="text-sm font-medium text-cyan-700">
            {formatMeters(preview.distanceMeters)}
          </span>
        </div>
        <div className="flex items-center justify-between border-t border-rgf-gray-100 pt-2">
          <span className="text-xs text-rgf-gray-500">Delta</span>
          <span
            className={`text-sm font-semibold ${
              delta >= 0 ? 'text-emerald-700' : 'text-red-700'
            }`}
          >
            {deltaPrefix}
            {formatMeters(Math.abs(delta))}
          </span>
        </div>
      </div>

      {(applied || rejected) && (
        <div
          className={`mx-4 mb-3 rounded-md p-2 text-xs flex items-start gap-2 ${
            applied
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              : 'bg-rgf-gray-50 text-rgf-gray-700 border border-rgf-gray-200'
          }`}
        >
          {applied ? (
            <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
          ) : (
            <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            <p className="font-medium">
              {applied ? 'Correction applied' : 'Correction rejected'}
            </p>
            {applied && (
              <p>Sync the daily summary so the report reflects it.</p>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="mx-4 mb-3 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium">
              {error.status === 403
                ? 'Not authorised'
                : error.status === 404
                ? 'Not found'
                : 'Request failed'}
            </p>
            <p>{error.message}</p>
          </div>
        </div>
      )}

      <div className="px-4 pb-4 flex items-center gap-2">
        {!applied && !rejected && (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={onReject}
              disabled={busy !== null}
              icon={
                busy === 'reject' ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <XCircle className="h-3.5 w-3.5" />
                )
              }
            >
              Reject
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={onApply}
              disabled={busy !== null}
              icon={
                busy === 'apply' ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                )
              }
            >
              Apply
            </Button>
          </>
        )}
        {applied && (
          <Button
            variant="primary"
            size="sm"
            onClick={onSync}
            disabled={busy !== null || !!syncDisabledReason}
            title={syncDisabledReason ?? undefined}
            icon={
              busy === 'sync' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCcw className="h-3.5 w-3.5" />
              )
            }
          >
            Sync daily summary
          </Button>
        )}
        {rejected && (
          <Button variant="secondary" size="sm" onClick={onDismiss}>
            Close
          </Button>
        )}
      </div>
    </div>
  );
};
